import { Row, Col, List, Space, Typography, Avatar, Tag, Badge, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, ToolOutlined, BuildOutlined, CalendarOutlined, PlusCircleOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { trpc } from '../lib/api';
import type { AnnouncementPriority, UserAnnouncementNotification, UserNotificationStatus } from '../types/announcements';
import { useMemo, useState, useCallback } from 'react';

// 新しいコンポーネントのインポート
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { EquipmentHealthChart } from '../components/dashboard/EquipmentHealthChart';
import { MaintenanceSummaryCard } from '../components/dashboard/MaintenanceSummaryCard';
import { AlertNotificationTabs } from '../components/dashboard/AlertNotificationTabs';
import { PartsSummaryCard } from '../components/dashboard/PartsSummaryCard';
import { LowStockPartsTable } from '../components/dashboard/LowStockPartsTable';
import { UserActivityLog } from '../components/dashboard/UserActivityLog';
import { OnboardingCard } from '../components/dashboard/OnboardingCard';
import { usePermission } from '../hooks/usePermission';

const { Text } = Typography;

const priorityColors: Record<AnnouncementPriority, string> = {
  low: 'default',
  normal: 'processing',
  high: 'warning',
  critical: 'error',
};

const priorityLabels: Record<AnnouncementPriority, string> = {
  low: '低',
  normal: '通常',
  high: '高',
  critical: '緊急',
};

const notificationStatusMeta: Record<UserNotificationStatus, { status: 'processing' | 'success' | 'default'; text: string }> = {
  unread: { status: 'processing', text: '未読' },
  read: { status: 'success', text: '既読' },
  dismissed: { status: 'default', text: '非表示' },
};

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}週間前`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}ヶ月前`;
  return `${Math.floor(diffDays / 365)}年前`;
}

function renderStatusBadge(status: UserNotificationStatus) {
  const meta = notificationStatusMeta[status];
  return <Badge status={meta.status} text={meta.text} />;
}

function extractNotificationSummary(notification: UserAnnouncementNotification): string | null {
  const metadata = notification.metadata as Record<string, unknown> | null;
  const summary = metadata?.['summary'];
  if (typeof summary === 'string') {
    const trimmed = summary.trim();
    if (trimmed.length > 0) {
      return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
    }
  }

  if (!notification.bodyMd) {
    return null;
  }

  const plain = notification.bodyMd
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[#>*_]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plain) {
    return null;
  }

  return plain.length > 80 ? `${plain.slice(0, 80)}…` : plain;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

type DashboardPart = {
  id: string;
  displayId: string;
  name: string;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  unit?: string | null;
  unitPrice?: string | null;
};

const normalizeDashboardPart = (value: unknown): DashboardPart | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    displayId: typeof value.displayId === 'string' ? value.displayId : '',
    name: typeof value.name === 'string' ? value.name : '',
    currentStockLevel: typeof value.currentStockLevel === 'number' ? value.currentStockLevel : null,
    optimalStockLevel: typeof value.optimalStockLevel === 'number' ? value.optimalStockLevel : null,
    unit: typeof value.unit === 'string' ? value.unit : null,
    unitPrice: typeof value.unitPrice === 'string' ? value.unitPrice : null,
  };
};

type DashboardActivity = {
  id: string;
  activityType: 'created' | 'updated' | 'deleted';
  entityType: 'equipment' | 'part' | 'work_schedule';
  entityId: string;
  entityName: string;
  entityDisplayId: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  timestamp: Date;
};

const normalizeDashboardActivity = (value: unknown): DashboardActivity | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.entityId !== 'string') {
    return null;
  }

  const activityType =
    value.activityType === 'created' || value.activityType === 'updated' || value.activityType === 'deleted'
      ? value.activityType
      : null;
  const entityType =
    value.entityType === 'equipment' || value.entityType === 'part' || value.entityType === 'work_schedule'
      ? value.entityType
      : null;

  if (!activityType || !entityType) {
    return null;
  }

  return {
    id: value.id,
    activityType,
    entityType,
    entityId: value.entityId,
    entityName: typeof value.entityName === 'string' ? value.entityName : '',
    entityDisplayId: typeof value.entityDisplayId === 'string' ? value.entityDisplayId : null,
    userId: typeof value.userId === 'string' ? value.userId : '',
    userName: typeof value.userName === 'string' ? value.userName : '',
    userEmail: typeof value.userEmail === 'string' ? value.userEmail : '',
    timestamp: new Date(typeof value.timestamp === 'string' ? value.timestamp : Date.now()),
  };
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { isManagerOrAdmin } = usePermission();

  // データ取得
  const { data: statistics, isLoading: statisticsLoading } = trpc.equipment.getStatistics.useQuery({});
  const { data: healthDistribution, isLoading: healthDistributionLoading } = trpc.equipment.getHealthDistribution.useQuery({});
  const { data: activitiesData, isLoading: activitiesLoading } = trpc.activity.getRecentActivities.useQuery({ limit: 20 });
  const { data: companyInfo } = trpc.company.getCompanyInfo.useQuery();
  const { data: announcementsData, isLoading: announcementsLoading } = trpc.announcementUser.list.useQuery({
    limit: 5,
    includeDismissed: false,
  });
  const { data: maintenanceStats, isLoading: maintenanceLoading } = trpc.schedule.getStatistics.useQuery();
  const { data: partsData, isLoading: partsLoading } = trpc.parts.list.useQuery({
    page: 1,
    limit: 1000, // 統計情報取得のため大量に取得
  });
  const { data: locationTree } = trpc.location.getTreeWithCounts.useQuery({});

  // オンボーディングカードの状態
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem('onboarding_dismissed') !== 'true';
  });

  const handleDismissOnboarding = useCallback(() => {
    localStorage.setItem('onboarding_dismissed', 'true');
    setShowOnboarding(false);
  }, []);

  // オンボーディングカードの表示条件
  const equipmentCount = statistics?.total ?? 0;
  const scheduleCount = maintenanceStats?.totalScheduled ?? 0;
  const locationCount = locationTree?.length ?? 0;

  // 設備1件以上 かつ スケジュール1件以上あれば非表示
  const shouldShowOnboardingCard = showOnboarding && (equipmentCount === 0 || scheduleCount === 0);

  const { data: headerImage } = trpc.company.getHeaderImage.useQuery(
    { companyId: companyInfo?.id || '' },
    { enabled: !!companyInfo?.id }
  );

  // ヘッダー画像の状態
  const headerImageDataResolved = headerImage !== undefined;
  const hasCustomHeaderImage = Boolean(headerImage?.imageUrl);
  const shouldShowDefaultHeaderImage = headerImageDataResolved && !hasCustomHeaderImage;

  // アナウンスと活動データ
  const latestAnnouncements = (announcementsData?.items ?? []) as UserAnnouncementNotification[];
  const unreadAnnouncementCount = announcementsData?.unreadCount ?? 0;
  const recentActivities = useMemo(
    () => (activitiesData?.activities ?? []).flatMap((activity) => {
      const normalized = normalizeDashboardActivity(activity);
      return normalized ? [normalized] : [];
    }),
    [activitiesData?.activities]
  );
  const normalizedPartsData = useMemo(
    () => ({
      items: (partsData?.items ?? []).flatMap((part) => {
        const normalized = normalizeDashboardPart(part);
        return normalized ? [normalized] : [];
      }),
      pagination: {
        total: partsData?.pagination?.total ?? 0,
      },
    }),
    [partsData]
  );
  const normalizedActivitiesData = useMemo(
    () => ({
      activities: recentActivities,
    }),
    [recentActivities]
  );

  // 健全度分布データの計算
  const healthDistributionData = useMemo(() => {
    if (!healthDistribution) return undefined;

    return [
      {
        name: '優良（健全度70-100%）',
        value: healthDistribution.excellent,
        color: token.colorSuccess,
        healthIndex: 'excellent',
      },
      {
        name: '良好（健全度50-70%）',
        value: healthDistribution.good,
        color: token.colorInfo,
        healthIndex: 'good',
      },
      {
        name: '注意（健全度30-50%）',
        value: healthDistribution.warning,
        color: token.colorWarning,
        healthIndex: 'warning',
      },
      {
        name: '危険（健全度0-30%）',
        value: healthDistribution.critical,
        color: token.colorError,
        healthIndex: 'critical',
      },
    ];
  }, [healthDistribution, token]);

  // アナウンスリストコンテンツ
  const announcementContent = (
    <div style={{ padding: '16px 0' }}>
      {!announcementsLoading && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, paddingLeft: 0 }}>
          未読: {unreadAnnouncementCount > 0 ? `${unreadAnnouncementCount}件` : 'ありません'}
        </Text>
      )}
      {latestAnnouncements.length > 0 ? (
        <List
          dataSource={latestAnnouncements}
          itemLayout="vertical"
          size="small"
          split
          renderItem={(item) => {
            const timeAgo = getTimeAgo(new Date(item.createdAt));
            const priority = item.announcement?.priority ?? null;
            const summary = extractNotificationSummary(item);
            return (
              <List.Item key={item.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/app/announcements')}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space size={8} wrap>
                    {renderStatusBadge(item.status)}
                    <Text type="secondary">{timeAgo}</Text>
                    {priority && <Tag color={priorityColors[priority]}>優先度: {priorityLabels[priority]}</Tag>}
                  </Space>
                  <Text strong>{item.title}</Text>
                  {summary && <Text type="secondary">{summary}</Text>}
                </Space>
              </List.Item>
            );
          }}
        />
      ) : (
        !announcementsLoading && <Text type="secondary">表示できるアナウンスはありません。</Text>
      )}
    </div>
  );

  // 活動リストコンテンツ
  const activityContent = (
    <div style={{ padding: '16px 0' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {recentActivities.length > 0 ? (
          recentActivities.map((activity) => {
            const timeAgo = getTimeAgo(new Date(activity.timestamp));

            const entityIcon = {
              equipment: <ToolOutlined />,
              part: <BuildOutlined />,
              work_schedule: <CalendarOutlined />,
            }[activity.entityType];

            const entityColor = {
              equipment: token.colorInfo,
              part: token.colorSuccess,
              work_schedule: token.colorWarning,
            }[activity.entityType];

            const actionText = {
              created: '作成しました',
              updated: '更新しました',
              deleted: '削除しました',
            }[activity.activityType];

            const actionIcon = {
              created: <PlusCircleOutlined style={{ color: token.colorSuccess }} />,
              updated: <EditOutlined style={{ color: token.colorInfo }} />,
              deleted: <DeleteOutlined style={{ color: token.colorError }} />,
            }[activity.activityType];

            const entityTypeName = {
              equipment: '設備',
              part: '部品・消耗品',
              work_schedule: '作業予定',
            }[activity.entityType];

            const navigateTo = () => {
              if (activity.activityType === 'deleted') {
                return;
              }
              const routes = {
                equipment: `/app/equipments/${activity.entityId}`,
                part: `/app/parts/${activity.entityId}`,
                work_schedule: `/app/schedule/${activity.entityId}`,
              };
              navigate(routes[activity.entityType]);
            };

            return (
              <div
                key={`${activity.entityType}-${activity.entityId}-${activity.activityType}`}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: token.colorFillQuaternary,
                  cursor: activity.activityType !== 'deleted' ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                }}
                onClick={navigateTo}
                onMouseEnter={(e) => {
                  if (activity.activityType !== 'deleted') {
                    e.currentTarget.style.background = token.colorFillTertiary;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = token.colorFillQuaternary;
                }}
              >
                <Space size="middle" style={{ width: '100%' }}>
                  <Avatar icon={<UserOutlined />} size="small" />
                  <div style={{ flex: 1 }}>
                    <Space size="small" style={{ marginBottom: '4px' }}>
                      {actionIcon}
                      <Text strong>{activity.userName}</Text>
                      <Text type="secondary">が</Text>
                    </Space>
                    <div>
                      <Space size="small">
                        <span style={{ color: entityColor }}>{entityIcon}</span>
                        <Text>
                          {entityTypeName}「
                          <Text strong style={{ color: entityColor }}>
                            {activity.entityName} ({activity.entityDisplayId || '-'})
                          </Text>
                          」を
                        </Text>
                        <Text>{actionText}</Text>
                      </Space>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {timeAgo}
                    </Text>
                  </div>
                </Space>
              </div>
            );
          })
        ) : (
          <Text type="secondary">最近の活動はありません。</Text>
        )}
      </Space>
    </div>
  );

  return (
    <div>
      {/* オンボーディングカード（初期セットアップガイド） */}
      {shouldShowOnboardingCard && (
        <OnboardingCard
          equipmentCount={equipmentCount}
          scheduleCount={scheduleCount}
          locationCount={locationCount}
          onDismiss={handleDismissOnboarding}
        />
      )}

      {/* ヘッダーセクション（KPIカードを含む） */}
      <DashboardHeader
        companyName={companyInfo?.name}
        headerImageUrl={headerImage?.imageUrl}
        hasCustomHeaderImage={hasCustomHeaderImage}
        shouldShowDefaultHeaderImage={shouldShowDefaultHeaderImage}
        statistics={statistics}
        statisticsLoading={statisticsLoading}
        isManagerOrAdmin={isManagerOrAdmin}
      />

      {/* チャートとサマリー */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <EquipmentHealthChart data={healthDistributionData} loading={healthDistributionLoading} />
        </Col>
        <Col xs={24} lg={12}>
          <MaintenanceSummaryCard statistics={maintenanceStats} loading={maintenanceLoading} />
        </Col>
      </Row>

      {/* 部品・消耗品在庫管理 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <PartsSummaryCard data={normalizedPartsData} loading={partsLoading} />
        </Col>
        <Col xs={24} lg={12}>
          <LowStockPartsTable data={normalizedPartsData} loading={partsLoading} />
        </Col>
      </Row>

      {/* ユーザー操作ログ */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <UserActivityLog data={normalizedActivitiesData} loading={activitiesLoading} limit={20} />
        </Col>
      </Row>

      {/* アラート＆通知タブ */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <AlertNotificationTabs
            announcementContent={announcementContent}
            activityContent={activityContent}
            unreadCount={unreadAnnouncementCount}
            loading={announcementsLoading || activitiesLoading}
          />
        </Col>
      </Row>
    </div>
  );
}
